import { Box, Button, Paper } from '@material-ui/core';
import { ReactElement, useState } from 'react';
import { connect } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';
import { Dispatch } from 'redux';

import { ComparisonProjectSelector } from '..';
import {
	SelectedProject,
	SelectedComparison,
	projectSelectorSelectProject,
	projectSelectorDeselectProject,
	projectSelectorSelectComparison,
	projectSelectorDeselectComparison,
	Bundle
} from '../../../redux';
import { Body1, Header4 } from '../../atoms';
import { SelectedProjectsDialog } from '../selected-projects-dialog/selected-projects-dialog.component';

import './project-selector.styles.scss';
import { useBrandName } from '../../../hooks';
import { useTranslation } from 'react-i18next';

export interface IProjectSelectorOwnProps {
	bundle: Bundle;
	selectedProjects: SelectedProject[];
	selectedComparisons: SelectedComparison[];
	onConfirm: () => void;
	onCancel: () => void;
}

export interface IProjectSelectorStateProps {
	selectProject: (project: SelectedProject) => void;
	deselectProject: (project: SelectedProject) => void;
	selectComparison: (comparison: SelectedComparison) => void;
	deselectComparison: (comparison: SelectedComparison) => void;
}

function ProjectSelectorView(
	props: IProjectSelectorOwnProps & IProjectSelectorStateProps
): ReactElement {
	const {
		selectedProjects,
		selectedComparisons,
		onConfirm,
		onCancel,
		selectProject,
		deselectProject,
		selectComparison,
		deselectComparison
	} = props;
	const { t } = useTranslation(['myBosatlas', 'general']);

	const brandName: string = useBrandName();
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);

	const totalSelected: number =
		selectedComparisons.length + selectedProjects.length;

	function onProjectSelect(
		project: SelectedProject,
		selected: boolean
	): void {
		if (selected) {
			selectProject(project);
		} else {
			deselectProject(project);
		}
	}

	function onComparisonSelect(
		comparison: SelectedComparison,
		selected: boolean
	): void {
		if (selected) {
			selectComparison(comparison);
		} else {
			deselectComparison(comparison);
		}
	}

	return (
		<Box className="project-selector">
			<Box className="project-selector-header">
				<Header4>{t('myBosatlas:folder.chooseMapsFolder')}</Header4>
				<Body1>
					{t('myBosatlas:folder.chooseMapsFolderAtlas', {
						brandName
					})}
				</Body1>
			</Box>
			<Box className="project-selector-content">
				<ComparisonProjectSelector
					projectSelection={selectedProjects}
					comparisonSelection={selectedComparisons}
					onProjectSelect={onProjectSelect}
					onComparisonSelect={onComparisonSelect}
				/>
			</Box>
			<Paper className="project-selector-footer" elevation={10}>
				<Button variant="outlined" color="primary" onClick={onCancel}>
					<CloseIcon /> {t('general:dialog.cancel')}
				</Button>
				<Box
					className="project-selector-footer-selection"
					onClick={() => setDialogOpen(!dialogOpen)}
				>
					<Body1 className="selected-projects">
						{t('myBosatlas:folder.amountChosenMaps', {
							amount: totalSelected
						})}
					</Body1>
					<ArrowDropDown className="arrow" />
				</Box>
				<Button
					onClick={onConfirm}
					variant="contained"
					color="primary"
					disabled={totalSelected === 0}
				>
					<AddIcon />
					{t('myBosatlas:folder.addToFolder')}
				</Button>
			</Paper>
			<SelectedProjectsDialog
				selectedProjects={selectedProjects}
				selectedComparisons={selectedComparisons}
				open={dialogOpen}
				deselectProject={deselectProject}
				deselectComparison={deselectComparison}
				onClose={() => setDialogOpen(false)}
			/>
		</Box>
	);
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		selectProject: (project: SelectedProject) =>
			dispatch(projectSelectorSelectProject(project)),
		deselectProject: (project: SelectedProject) =>
			dispatch(projectSelectorDeselectProject(project)),
		selectComparison: (comparison: SelectedComparison) =>
			dispatch(projectSelectorSelectComparison(comparison)),
		deselectComparison: (comparison: SelectedComparison) =>
			dispatch(projectSelectorDeselectComparison(comparison))
	};
}

export const ProjectSelector = connect(
	undefined,
	mapDispatchToProps
)(ProjectSelectorView);
